package com.schoolconnect.app.feature.notices

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.foundation.Image
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import com.schoolconnect.app.util.decodeImageBitmap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.downloadFile
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.files.appendMimeTypeExtension
import kotlinx.coroutines.launch
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

@Composable
internal fun ViewNoticeDialog(
    notice: NoticeRowUi?,
    detail: NoticeDetailUi?,
    isLoading: Boolean,
    errorMessage: String,
    onDismiss: () -> Unit,
    onView: (String) -> Unit,
    loadDetail: Boolean = true
) {
    if (notice == null) return
    if (loadDetail) {
        androidx.compose.runtime.LaunchedEffect(notice.backendId) { onView(notice.backendId) }
    }
    val content = detail ?: NoticeDetailUi(notice.id, notice.title, "", notice.audience, notice.expiryDate, notice.status, "—", "—", notice.attachmentFileIds)
    AppDialogScaffold(
        onDismiss = onDismiss,
        size = AppDialogSize.Narrow,
        maxDialogHeight = 820.dp,
        isLoading = isLoading,
        loadingTitle = "Loading notice details"
    ) {
        AppDialogHeader(
            title = "View Notice",
            subtitle = "Review notice details and communication content.",
            onClose = onDismiss,
            icon = Res.drawable.megaphone,
            iconContentDescription = "Notice",
            colors = AppDialogDefaultColors
        )
        Column(
            Modifier.fillMaxWidth().weight(1f, fill = false).heightIn(max = 630.dp).verticalScroll(rememberScrollState()).padding(horizontal = 28.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp)
        ) {
            if (isLoading) {
                Text("Loading notice details…", color = AppMuted, fontSize = 13.sp)
            } else if (errorMessage.isNotBlank()) {
                AppDialogErrorMessage(errorMessage)
            } else {
                ViewAuditCard(content)
                ViewInformationCard(content)
                ViewContentCard(content)
                ViewAttachmentsCard(content)
            }
        }
        Row(Modifier.fillMaxWidth().border(BorderStroke(1.dp, AppBorder), RoundedCornerShape(0.dp)).padding(horizontal = 28.dp, vertical = 14.dp), horizontalArrangement = Arrangement.End) {
            AppOutlinedButton(onClick = onDismiss, modifier = Modifier.width(108.dp).height(42.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) { Text("Close", fontSize = 14.sp, fontWeight = FontWeight.SemiBold) }
        }
    }
}

@Composable private fun ViewAuditCard(detail: NoticeDetailUi) {
    ViewCard {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(20.dp)) {
            ViewAuditColumn("Created By", detail.createdBy, detail.createdAt, Modifier.weight(1f))
            VerticalDivider(color = AppBorder, modifier = Modifier.height(76.dp))
            ViewAuditColumn("Updated By", detail.updatedBy, detail.updatedAt, Modifier.weight(1f))
        }
    }
}

@Composable private fun ViewAuditColumn(label: String, name: String, timestamp: String, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(7.dp)) {
        Text(label, color = AppNavy, fontSize = 12.sp, fontWeight = FontWeight.Bold)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Icon(painterResource(Res.drawable.user), null, tint = AppNavy, modifier = Modifier.size(22.dp))
            Text(name, color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.Medium)
        }
        Text(timestamp, color = AppMuted, fontSize = 12.sp)
    }
}

@Composable private fun ViewInformationCard(notice: NoticeDetailUi) {
    ViewCard {
        ViewCardTitle(Res.drawable.document, "Notice Information")
        Spacer(Modifier.height(12.dp))
        ViewInfoRow("Notice Title", notice.title)
        ViewInfoRow("Audience", notice.audience)
        ViewInfoRow("Expiry Date", notice.expiryDate)
        ViewInfoRow("Status", notice.status, statusColor(notice.status))
    }
}

@Composable private fun ViewInfoRow(label: String, value: String, valueColor: Color = AppNavy) {
    Row(Modifier.fillMaxWidth().padding(vertical = 5.dp), verticalAlignment = Alignment.Top) {
        Text(label, modifier = Modifier.width(122.dp), color = AppNavy, fontSize = 13.sp)
        Text(":", modifier = Modifier.width(18.dp), color = AppNavy, fontSize = 13.sp)
        Text(value, modifier = Modifier.weight(1f), color = valueColor, fontSize = 13.sp, fontWeight = if (valueColor != AppNavy) FontWeight.SemiBold else FontWeight.Normal)
    }
}

@Composable private fun ViewContentCard(notice: NoticeDetailUi) {
    ViewCard {
        Text("Notice Content", color = AppNavy, fontSize = 15.sp, fontWeight = FontWeight.Bold)
        Spacer(Modifier.height(10.dp))
        Text(notice.content.ifBlank { "—" }, color = AppNavy, fontSize = 13.sp, lineHeight = 20.sp)
    }
}

@Composable private fun ViewAttachmentsCard(notice: NoticeDetailUi) {
    if (notice.attachments.isEmpty()) return
    val coroutineScope = androidx.compose.runtime.rememberCoroutineScope()
    var downloadingAttachmentId by remember { mutableStateOf<String?>(null) }
    ViewCard {
        Text("Attachments", color = AppNavy, fontSize = 15.sp, fontWeight = FontWeight.Bold)
        Spacer(Modifier.height(10.dp))
        notice.attachments.forEachIndexed { index, attachment ->
            ViewAttachment(attachment, onDownload = {
                if (attachment.url.isNotBlank()) {
                    coroutineScope.launch {
                        downloadingAttachmentId = attachment.fileId.ifBlank { attachment.name }
                        try {
                            FileUploadRepository.downloadBytes(attachment.url).onSuccess { bytes ->
                                downloadFile(appendMimeTypeExtension(attachment.name, attachment.mimeType), bytes, attachment.mimeType)
                            }
                        } finally {
                            downloadingAttachmentId = null
                        }
                    }
                }
            }, downloadingAttachmentId = downloadingAttachmentId)
            if (index < notice.attachments.lastIndex) Spacer(Modifier.height(8.dp))
        }
    }
}

@Composable private fun ViewAttachment(attachment: NoticeAttachmentDetailUi, onDownload: () -> Unit, downloadingAttachmentId: String? = null) {
    val kind = attachmentKind(attachment)
    val image = remember(attachment.fileId, attachment.previewBytes) { attachment.previewBytes?.let(::decodeImageBitmap) }
    val background = when (kind) { "IMAGE" -> AppSuccessSoft; "VIDEO" -> AppPrimarySoft; else -> AppDangerSoft }
    Row(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
        if (image != null) {
            Image(image, contentDescription = attachment.name, modifier = Modifier.size(52.dp).background(AppSurface, RoundedCornerShape(8.dp)), contentScale = ContentScale.Crop)
        } else {
            Box(Modifier.size(34.dp).background(background, RoundedCornerShape(8.dp)), contentAlignment = Alignment.Center) { Icon(painterResource(Res.drawable.document), null, tint = AppPrimary, modifier = Modifier.size(20.dp)) }
        }
        Spacer(Modifier.width(10.dp))
        Column(Modifier.weight(1f)) { Text(attachment.name, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.Medium); Text("${kindLabel(kind, attachment)}  •  ${attachment.sizeLabel}", color = AppMuted, fontSize = 11.sp) }
        if (downloadingAttachmentId == attachment.fileId.ifBlank { attachment.name }) {
            CircularProgressIndicator(color = AppPrimary, strokeWidth = 2.dp, modifier = Modifier.size(20.dp))
        } else {
            Icon(painterResource(Res.drawable.download), "Download ${attachment.name}", tint = if (attachment.url.isNotBlank()) AppPrimary else AppMuted, modifier = Modifier.size(20.dp).then(if (attachment.url.isNotBlank()) Modifier.clickable(onClick = onDownload) else Modifier))
        }
    }
}

private fun attachmentKind(attachment: NoticeAttachmentDetailUi): String = when {
    attachment.mimeType.startsWith("image/", ignoreCase = true) -> "IMAGE"
    attachment.mimeType.startsWith("video/", ignoreCase = true) -> "VIDEO"
    attachment.mimeType.contains("pdf", ignoreCase = true) -> "PDF"
    else -> attachment.name.substringAfterLast('.', "FILE").uppercase().ifBlank { "FILE" }
}

private fun kindLabel(kind: String, attachment: NoticeAttachmentDetailUi): String = when (kind) {
    "IMAGE" -> "Image"
    "VIDEO" -> "Video"
    "PDF" -> "PDF"
    else -> attachment.mimeType.substringAfterLast('/', kind).uppercase()
}

@Composable private fun ViewCard(content: @Composable ColumnScope.() -> Unit) {
    Column(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(14.dp), content = content)
}

@Composable private fun ViewCardTitle(icon: DrawableResource, title: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(9.dp)) {
        Box(Modifier.size(34.dp).background(AppPrimarySoft, RoundedCornerShape(8.dp)), contentAlignment = Alignment.Center) { Icon(painterResource(icon), null, tint = AppPrimary, modifier = Modifier.size(20.dp)) }
        Text(title, color = AppNavy, fontSize = 15.sp, fontWeight = FontWeight.Bold)
    }
}

private fun statusColor(status: String): Color = when (status) { "Draft" -> Color(0xFFFF7A00); "Expired" -> AppDanger; else -> AppSuccess }
